import { useEffect, useRef, useState } from "react";
import { squareApiService } from "../services/squareApiService";
import { isSquareConflict, squareConflictMessage } from "../services/squareErrors";
import {
  allowedSquareAuthorizationUrl, createSquareAttempt, isSquareCompletionMessage,
  markSquareSameTab, readSquareAttempt, removeSquareAttempt, type SquareAttempt,
} from "../services/squareBrowserFlow";

type Pending = { attempt: SquareAttempt; popup: Window | null; url?: string; controller: AbortController };

export function useSquareConnect(businessId: string, locationId: string, userId: string, refresh: () => void) {
  const pending = useRef<Pending | null>(null);
  const refreshRef = useRef(refresh);
  const [busy, setBusy] = useState(false);
  const [fallback, setFallback] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => { refreshRef.current = refresh; }, [refresh]);
  useEffect(() => {
    function finish(message: string) {
      const current = pending.current;
      if (!current) return;
      current.controller.abort();
      removeSquareAttempt(current.attempt.attemptId);
      pending.current = null;
      setBusy(false);
      setFallback(false);
      setMessage(message);
      refreshRef.current();
    }
    function receive(event: MessageEvent) {
      const current = pending.current;
      if (!current || !isSquareCompletionMessage(event, current.popup, current.attempt.attemptId)) return;
      finish(event.data.result === "cancelled" ? "Square connection was cancelled." :
        event.data.result === "error" ? "Couldn't finish connecting Square. Please try again." :
        "Square authorization finished. Checking connection status.");
    }
    window.addEventListener("message", receive);
    const timer = window.setInterval(() => {
      const current = pending.current;
      if (!current) return;
      if (!readSquareAttempt(current.attempt.attemptId)) {
        current.popup?.close();
        finish("The Square connection attempt expired. Please start again.");
      } else if (current.popup?.closed) {
        finish("Square window closed. Check the connection status before trying again.");
      }
    }, 1000);
    return () => {
      window.removeEventListener("message", receive);
      window.clearInterval(timer);
      const current = pending.current;
      if (current && !current.url) {
        current.controller.abort();
        current.popup?.close();
        removeSquareAttempt(current.attempt.attemptId);
      }
      pending.current = null;
    };
  }, []);

  const connect = async () => {
    if (pending.current) return;
    setMessage("");
    let current: Pending | undefined;
    try {
      const attempt = createSquareAttempt(businessId, locationId, userId);
      // Open synchronously after persisting proof so the popup inherits sessionStorage.
      let popup: Window | null = null;
      try { popup = window.open("about:blank", "_blank", "popup,width=600,height=760"); } catch { /* Offer same-tab fallback. */ }
      current = { attempt, popup, controller: new AbortController() };
      pending.current = current;
      setBusy(true);
      const dto = await squareApiService.connect(businessId, {
        attempt_id: attempt.attemptId, browser_verifier: attempt.browserVerifier, location_id: locationId,
      }, current.controller.signal);
      if (pending.current !== current) return;
      current.url = allowedSquareAuthorizationUrl(dto.authorization_url);
      if (popup && !popup.closed) popup.location.href = current.url;
      else setFallback(true);
    } catch (error) {
      if (current && pending.current !== current) return;
      current?.popup?.close();
      if (current) removeSquareAttempt(current.attempt.attemptId);
      pending.current = null;
      setBusy(false);
      setFallback(false);
      setMessage(isSquareConflict(error) ? squareConflictMessage : "Couldn't start connecting Square. Please try again.");
    }
  };

  const continueInTab = () => {
    const current = pending.current;
    if (!current?.url) return;
    try {
      markSquareSameTab(current.attempt);
      window.location.assign(current.url);
    } catch {
      removeSquareAttempt(current.attempt.attemptId);
      pending.current = null;
      setBusy(false);
      setFallback(false);
      setMessage("Couldn't continue in this tab. Please allow browser storage and start again.");
    }
  };
  return { connect, continueInTab, busy, fallback, message };
}
